import { useState } from 'react';
import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  List,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
  type RaRecord,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import type { PromoCode } from '../dataProvider/types';
import {
  LIFECYCLE_COLOR,
  LIFECYCLE_LABEL,
  formatEffect,
  formatRedemptions,
  formatWindow,
  promoLifecycle,
} from './promoState';

/**
 * Promo codes list - the second Phase 4 surface and a managed CRUD resource
 * (Charities-shaped). Promo codes are DEFINITIONS in a catalogue: redemption is
 * not modelled here, and usedCount is a displayed number a future engine writes
 * back (D-promo-codes). Codes are deactivated, never deleted (a code with a
 * used-count history is kept), so there is no delete anywhere.
 *
 * Region-scoped by the access SCOPE through the one marketsForScope mapping (UK
 * scope shows uk codes, US scope us, GLOBAL both). The Create affordance and route
 * are gated promo_codes:issue (value-committing, super_admin); the per-row Edit is
 * gated promo_codes:write (value-reducing, editor and up).
 */

/** Active/Inactive as a chip — the soft-disable flag (deactivate-not-delete). */
const ActiveChip = () => {
  const record = useRecordContext<PromoCode>();
  const active = record?.active === true;
  return (
    <Chip
      size="small"
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
    />
  );
};

const promoFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
];

const PromoListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  // Create commits value, so it is gated promo_codes:issue (super_admin), not the
  // value-reducing promo_codes:write an editor holds.
  const canIssue = hasAccess(
    { role, accessibleRegions },
    { capability: 'promo_codes:issue' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canIssue && <CreateButton />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper; `asOf`
 *  injects the clock the lifecycle chip derives from. */
export const PromoCodeListView = ({ asOf }: { asOf?: number }) => {
  const { region } = useRegion();
  const { role, accessibleRegions } = useAccessContext();
  // One reference clock for the derived lifecycle chips, captured once.
  const [now] = useState<number>(() => asOf ?? Date.now());
  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'promo_codes:write' },
  );

  return (
    <List
      resource="promoCodes"
      title="Promo codes"
      // Scope, not activeRegion: GLOBAL shows both markets (marketsForScope).
      filter={{ region }}
      filters={promoFilters}
      actions={<PromoListActions />}
      sort={{ field: 'code', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      {/* No row navigation and no bulk actions: crucially no bulk delete, codes
          are deactivated never deleted. Edit is a per-row, capability-gated link. */}
      <Datagrid rowClick={false} bulkActionButtons={false}>
        <TextField source="code" label="Code" />
        <FunctionField
          label="Market"
          sortBy="market"
          render={(record: RaRecord) => (record.market as string).toUpperCase()}
        />
        <FunctionField
          label="Effect"
          render={(record: RaRecord) => formatEffect(record as PromoCode)}
        />
        <FunctionField
          label="Window"
          render={(record: RaRecord) => formatWindow(record as PromoCode)}
        />
        <FunctionField
          label="Redemptions"
          render={(record: RaRecord) => formatRedemptions(record as PromoCode)}
        />
        <FunctionField label="Status" render={() => <ActiveChip />} />
        <FunctionField
          label="Lifecycle"
          render={(record: RaRecord) => {
            const state = promoLifecycle(record as PromoCode, now);
            return (
              <Chip
                size="small"
                variant="outlined"
                label={LIFECYCLE_LABEL[state]}
                color={LIFECYCLE_COLOR[state]}
              />
            );
          }}
        />
        {canWrite && <EditButton />}
      </Datagrid>
    </List>
  );
};

/** Gated entry point registered as the `promoCodes` Resource's list. */
export const PromoCodeList = () => (
  <RequireAccess capability="promo_codes:read">
    <PromoCodeListView />
  </RequireAccess>
);
